import { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { openAuthenticatedBillingPortal } from "@/lib/accountApi";
import { openExternal } from "@/lib/openExternal";
import { PLANOS_URL } from "@/lib/billing";

const SUPPORT_URL =
  "https://wa.me/5511939204456?text=Oi!%20Preciso%20de%20ajuda%20com%20minha%20assinatura%20ONDIET.";

/**
 * SubscriptionBlockScreen — overlay full-screen que cobre o app quando a
 * assinatura está atrasada há >= 7 dias (item D). Sem X, sem como fechar:
 * só regularizar. Ao pagar, o webhook volta status→active e o realtime do
 * useSubscriptionStatus destrava sozinho (o AppLayout para de renderizar isto).
 *
 * Cores via tokens de tema (dark-mode safe).
 */
export const SubscriptionBlockScreen = ({ canceled = false }: { canceled?: boolean }) => {
  const { signOut } = useAuth();
  const [portalError, setPortalError] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);

  const manageSubscription = async () => {
    setPortalError(null);
    if (canceled) {
      openExternal(PLANOS_URL);
      return;
    }
    setOpeningPortal(true);
    try {
      await openAuthenticatedBillingPortal();
    } catch (error) {
      setPortalError(
        error instanceof Error
          ? error.message
          : "Não foi possível abrir a gestão da assinatura agora.",
      );
    } finally {
      setOpeningPortal(false);
    }
  };
  // Trava o scroll do body enquanto a tela de bloqueio está montada
  // (mesmo padrão do SubscriptionCanceledScreen).
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  return (
    <div
      data-testid="subscription-block-screen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="block-screen-title"
      aria-describedby="block-screen-desc"
      className="fixed inset-0 z-[300] flex items-center justify-center bg-background px-6"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent dark:bg-accent/50">
          <AlertTriangle className="h-7 w-7 text-muted-foreground dark:text-muted-foreground" />
        </div>
        <h1 id="block-screen-title" className="text-lg font-semibold text-foreground">
          {canceled ? "Assinatura encerrada" : "Pagamento pendente"}
        </h1>
        <p id="block-screen-desc" className="mt-2 text-sm text-muted-foreground">
          {canceled
            ? "Sua assinatura foi encerrada. Você pode assinar novamente quando quiser."
            : "Sua assinatura precisa de atenção e o acesso foi pausado. Gerencie a assinatura para voltar a usar o app normalmente."}
        </p>
        <button
          type="button"
          data-testid="subscription-regularizar"
          onClick={() => void manageSubscription()}
          disabled={openingPortal}
          className="mt-6 w-full rounded-lg bg-foreground py-3 text-sm font-semibold text-background border border-border transition-all active:scale-[0.98] active:opacity-90"
        >
          {openingPortal ? "Abrindo…" : "Gerenciar assinatura"}
          {!openingPortal && <ExternalLink className="ml-2 inline h-4 w-4" />}
        </button>
        {portalError && (
          <p role="alert" className="mt-3 text-xs text-destructive">
            {portalError}
          </p>
        )}
        <button
          type="button"
          onClick={() => openExternal(SUPPORT_URL)}
          className="mt-4 w-full rounded-lg border border-border py-3 text-sm font-medium text-foreground transition-all active:scale-[0.98]"
        >
          Falar com o suporte
        </button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-4 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Sair da conta
        </button>
      </div>
    </div>
  );
};

SubscriptionBlockScreen.displayName = "SubscriptionBlockScreen";
